interface Specification {
  id: string
  label: string
  value: string
}

interface SpecsSectionProps {
  specifications: Specification[]
}

export default function SpecsSection({ specifications }: SpecsSectionProps) {
  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-secondary/30 reveal">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2 className="playfair-header text-3xl sm:text-4xl md:text-5xl text-foreground mb-3 sm:mb-4">
            Technical Specs
          </h2>
          <p className="text-foreground/70 text-base sm:text-lg">
            Material provenance and direct-from-maker pricing.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Left: System Specifications */}
          <div className="space-y-4">
            <h3 className="mono-data text-xs sm:text-sm uppercase tracking-widest text-foreground/60 mb-4">
              System Specifications
            </h3>
            <div className="space-y-3">
              {specifications.map((spec) => (
                <div 
                  key={spec.id}
                  className="bg-card p-3 sm:p-4 rounded-lg sm:rounded-xl grid-border hover:border-primary/50 transition-colors duration-300"
                >
                  <div className="flex justify-between items-center gap-3">
                    <div className="mono-data text-xs uppercase tracking-widest text-foreground/60">
                      {spec.label}
                    </div>
                    <div className="mono-data text-xs sm:text-sm font-bold text-foreground text-right">
                      {spec.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Direct from Maker Comparison */}
          <div className="space-y-4">
            <h3 className="mono-data text-xs sm:text-sm uppercase tracking-widest text-foreground/60 mb-4">
              Direct from Maker
            </h3>
            
            {/* Artisan Quality (Our Price) */}
            <div className="glass-panel rounded-lg sm:rounded-xl p-4 sm:p-6 grid-border relative overflow-hidden">
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-primary text-primary-foreground px-2 py-1 rounded-full mono-data text-xs uppercase tracking-wider">
                DIRECT
              </div>
              
              <div className="space-y-3">
                <div>
                  <div className="mono-data text-xs uppercase tracking-wider text-foreground/60 mb-1">
                    ARTISAN QUALITY
                  </div>
                  <div className="mono-data text-3xl sm:text-4xl font-bold text-foreground">
                    £38.88
                  </div>
                </div>
                
                <ul className="space-y-2 mono-data text-xs sm:text-sm text-foreground/70">
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-success shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Hand-sculpted by Bali artisans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-success shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Reclaimed coconut leaf material</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-success shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>7-10 Working Days tracked delivery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-success shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>FREE UK SHIPPING</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-success shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Serialised Drop: 1/1,000 Only</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* High-Street Retail */}
            <div className="bg-card/50 rounded-lg sm:rounded-xl p-4 sm:p-6 grid-border opacity-70 relative">
              <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-muted text-muted-foreground px-2 py-1 rounded-full mono-data text-xs uppercase tracking-wider">
                RETAIL
              </div>
              
              <div className="space-y-3">
                <div>
                  <div className="mono-data text-xs uppercase tracking-wider text-foreground/60 mb-1">
                    HIGH-STREET RETAIL
                  </div>
                  <div className="mono-data text-3xl sm:text-4xl font-bold text-foreground line-through">
                    £59.99
                  </div>
                </div>
                
                <ul className="space-y-2 mono-data text-xs sm:text-sm text-foreground/50">
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Retail markup included</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Mass-produced alternatives</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Longer shipping times</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Mass-market availability</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Savings Callout */}
            <div className="p-3 sm:p-4 bg-success/10 rounded-lg sm:rounded-xl grid-border border-success/30">
              <div className="mono-data text-xs uppercase tracking-wider text-success font-bold mb-1">
                YOU SAVE 35%
              </div>
              <p className="text-xs sm:text-sm text-foreground/80">
                By ordering direct, you save <span className="font-bold">£21.11</span> compared to high-street retail.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}